import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { SystemConstant } from "const";

const OTPInput = ({ otpLength, errorContent, onChange, disabled, ...otherProps }) => {
  const inputRefsArray = useRef(new Array(otpLength).fill(null));
  const [otpValues, setOtpValues] = useState(new Array(otpLength).fill(""));
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleDigitChange = (event, index) => {
    const targetValue = event.target.value;

    // Ignore non-numeric values
    if (!event.target.validity.valid) return;

    // This is a "mobile paste" event, the whole value has been provided
    if (targetValue.length === otpLength) {
      handlePastedOtp(targetValue);
      return;
    }

    setOtpValues(updateArrayAtIndex(otpValues, targetValue, index));

    // Auto advance to next input field in the set
    const nextIndex = index + 1;
    if (nextIndex < otpLength) {
      setCurrentIndex(nextIndex);
      inputRefsArray.current[nextIndex]?.focus();
    } else {
      setCurrentIndex(-1);
    }
  };

  const handleBackspace = (event, index) => {
    if (event.key !== "Backspace") return;

    const prevIndex = Math.max(index - 1, 0);
    inputRefsArray.current[prevIndex]?.focus();
    setOtpValues(updateArrayAtIndex(otpValues, "", prevIndex));
    setCurrentIndex(prevIndex);
  };

  const handleFocus = (_, index) => {
    setCurrentIndex(index);
  };

  const handlePasteEvent = event => {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData("text").trim();
    handlePastedOtp(pastedText);
  };

  const handlePastedOtp = pastedText => {
    if (pastedText.match(new RegExp(`^[0-9]{${otpLength}}$`))) {
      setOtpValues(pastedText.split(""));
      setCurrentIndex(-1);
    }
  };

  useEffect(() => {
    if (inputRefsArray.current[0]) {
      inputRefsArray.current[0].focus();
    }
  }, [inputRefsArray]);

  useEffect(() => {
    if (currentIndex === -1) {
      inputRefsArray.current.forEach(inputRef => {
        inputRef?.blur();
      });
    } else {
      setOtpValues(otpValues.map((value, index) => (index < currentIndex ? value : "")));
    }
  }, [currentIndex]);

  useEffect(() => {
    if (onChange) {
      onChange(otpValues.join(""));
    }
  }, [otpValues]);

  return (
    <>
      <div className="d-flex gap-2 flex-wrap align-items-center justify-content-center w-100">
        {inputRefsArray.current.map((_, index) => (
          <input
            ref={element => (inputRefsArray.current[index] = element)}
            key={index}
            type="text"
            className="form-control text-center"
            style={{ width: "42px", height: "42px", borderRadius: "8px", padding: "9px" }}
            value={otpValues[index]}
            onChange={event => handleDigitChange(event, index)}
            onKeyDown={event => handleBackspace(event, index)}
            onFocus={event => handleFocus(event, index)}
            onPaste={handlePasteEvent}
            disabled={disabled}
            aria-label={`Enter OTP code, part ${index + 1} of ${otpLength}`}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={otpLength}
            {...otherProps}
          />
        ))}
      </div>

      {Boolean(errorContent) && (
        <p className="text-danger text-center small mt-2" style={{ whiteSpace: "pre-line" }}>
          {errorContent}
        </p>
      )}
    </>
  );
};

OTPInput.propTypes = {
  otpLength: PropTypes.number,
  errorContent: PropTypes.string,
};

OTPInput.defaultProps = {
  otpLength: SystemConstant.DEFAULT_OTP_LENGTH,
  errorContent: null,
  onChange: () => {},
};

export default OTPInput;

const updateArrayAtIndex = (array = [], newValue, index) => [
  ...array.slice(0, index),
  newValue,
  ...array.slice(index + 1),
];
